import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  QrCode, ChevronRight, CheckCircle2, Loader2, RotateCcw,
  HardHat, FolderTree, Briefcase, ShieldCheck, FileText, Users, Bell,
} from "lucide-react";
import { showRoutingConfirmation } from "@/lib/routingToast";

/**
 * TryToolboxTalk — interactive 4-step Toolbox Talk QR Flow.
 *
 * Goal (per user direction): let prospects EXPERIENCE the workflow —
 * REAL submission to `/api/meetings`, real RoutingAnimation trigger,
 * 15-20s end-to-end. Operationally elegant; NOT cinematic.
 *
 *   Step 1 — Scan QR
 *   Step 2 — Pick Job / Crew / Topic (tap-tiles, no typing)
 *   Step 3 — Tap Complete
 *   Step 4 — Cascading operational aftermath (notifications, dashboards,
 *            PDF, training, routing) — the moment that proves "the
 *            system actually did something".
 *
 * The real backend record persists. After cascade, prospects can reset
 * and run it again — or step inside via the role tiles below.
 */
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const JOBS = [
  { number: "FO-1001", name: "SR-22 Widening · MOT" },
  { number: "FO-1002", name: "Industrial Site Dev" },
  { number: "FO-1003", name: "Municipal Utility" },
  { number: "FO-1004", name: "Airport Taxiway Rehab" },
];

const CREWS = [
  { id: "crew-a", label: "Crew A · 9 on shift", count: 9 },
  { id: "crew-b", label: "Crew B · 7 on shift", count: 7 },
  { id: "crew-c", label: "Crew C · 11 on shift", count: 11 },
];

const TOPICS = [
  { key: "trench",     label: "Trench Safety",      hazards: "Cave-in, struck-by, atmospheric, access/egress" },
  { key: "fall",       label: "Fall Protection",    hazards: "Edge, anchor points, harness inspection, opening covers" },
  { key: "silica",     label: "Silica · Table 1",   hazards: "Respirable crystalline silica, water-on-saw, vacuum filtration" },
  { key: "heat",       label: "Heat Stress",        hazards: "Hydration, shade, work/rest cycle, acclimatization" },
];

// Cinematic but CONTROLLED aftermath. One event ~480ms apart.
const CASCADE = [
  { icon: Bell,         color: "#FF6A00", label: "Superintendent notified",            sub: "Pushed to Demo Super · field tablet"            },
  { icon: ShieldCheck,  color: "#65DA76", label: "Safety dashboard updated",           sub: "Toolbox Talks · attendance counted"             },
  { icon: Users,        color: "#FFD000", label: "Crew training records synced",       sub: "Per-employee record · QR sign-off captured"     },
  { icon: FileText,     color: "#65DA76", label: "PDF generated",                      sub: "Auto-routed · PM Folder + Safety Folder + Job"  },
  { icon: FolderTree,   color: "#FF6A00", label: "Compliance status updated",          sub: "OSHA-defensible record · 14d backup mirror"     },
  { icon: Briefcase,    color: "#65DA76", label: "PM notification pulse sent",         sub: "Project Manager queue · read-receipt tracked"   },
];

export default function TryToolboxTalk() {
  const [step, setStep]     = useState(0);   // 0 scan · 1 pick · 2 confirm · 3 aftermath · 4 done
  const [job, setJob]       = useState(null);
  const [crew, setCrew]     = useState(null);
  const [topic, setTopic]   = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [doneAt, setDoneAt] = useState(0);
  const [cascadeIdx, setCascadeIdx] = useState(0);
  const startRef = useRef(0);

  const reset = () => {
    setStep(0);
    setJob(null); setCrew(null); setTopic(null);
    setSubmitting(false);
    setDoneAt(0);
    setCascadeIdx(0);
  };

  // Aftermath cascade timer
  useEffect(() => {
    if (step !== 3) return;
    if (cascadeIdx >= CASCADE.length) {
      const t = setTimeout(() => setStep(4), 700);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setCascadeIdx((i) => i + 1), 480);
    return () => clearTimeout(t);
  }, [step, cascadeIdx]);

  const allPicked = !!job && !!crew && !!topic;

  // Step 1 → Step 2 (after QR "scan")
  const onScan = () => {
    startRef.current = Date.now();
    setStep(1);
    // Auto-advance hint — pre-selecting nothing; user needs to tap each.
  };

  // Step 3 → submit and start aftermath
  const onSubmit = async () => {
    if (!allPicked || submitting) return;
    setSubmitting(true);
    const now = new Date();
    const meetingDate = now.toISOString().slice(0, 10);
    const meetingTime = now.toTimeString().slice(0, 5);
    const payload = {
      project_name:    job.name,
      project_number:  job.number,
      location:        job.name + " · Site",
      meeting_date:    meetingDate,
      meeting_time:    meetingTime,
      conducted_by:    "Demo Foreman",
      topic:           topic.label,
      topic_category:  topic.key,
      hazards_reviewed: topic.hazards,
      discussion_notes: "Toolbox Talk completed via FORGEDOPS QR demo workflow. " + crew.label + ".",
      attendees:        Array.from({ length: crew.count }, (_, i) => ({
        name: "Crew Member " + (i + 1),
        signature: "[QR-signed]",
      })),
    };

    try {
      const res = await axios.post(`${API}/meetings`, payload);
      // Persist a lightweight record of this submission so the role-portal
      // pages can show a "your demo submission" mission notification.
      try {
        const dests = ["PM Folder", "Safety Folder", "Job Folder", "Admin Records", "Backup"];
        window.localStorage.setItem("forgeops.demo.lastSubmission", JSON.stringify({
          kind: "Toolbox Talk",
          meetingId: res?.data?.id || "",
          topic: topic.label,
          topicCategory: topic.key,
          crewLabel: crew.label,
          attendees: crew.count,
          jobNumber: job.number,
          jobName: job.name,
          routedTo: dests,
          at: new Date().toISOString(),
        }));
        // Clear any stale dismiss flag so the banner shows again for this fresh submit
        window.sessionStorage.removeItem("forgeops.demo.lastSubmission.dismissed");
      } catch { /* */ }
      // Real record persisted. Now run the cascade + cinematic routing.
      showRoutingConfirmation("Toolbox Talk", {
        auto_routed_to: ["PM Folder", "Safety Folder", "Job Folder", "Admin Records", "Backup"],
      });
      // Notify any operational chain panels visible elsewhere so they
      // briefly switch to the matching scenario and illuminate node-
      // by-node, then settle back into auto-tour.
      try {
        window.dispatchEvent(
          new CustomEvent("forgeops:submission", {
            detail: {
              kind: "toolbox-talk",
              meetingId: res?.data?.id || "",
              topic: topic.label,
              jobNumber: job.number,
              attendees: crew.count,
              at: Date.now(),
            },
          })
        );
      } catch { /* */ }
      setDoneAt(Date.now());
      setStep(3);
      setCascadeIdx(0);
    } catch (err) {
      toast.error("Submission failed — try once more in a moment.");
      setSubmitting(false);
    }
  };

  const elapsed = doneAt && startRef.current
    ? ((doneAt - startRef.current) / 1000).toFixed(1) + "s"
    : "—";

  return (
    <section
      data-testid="try-toolbox-talk"
      className="max-w-5xl mx-auto px-5 sm:px-8 py-12 sm:py-16"
    >
      {/* Eyebrow + headline */}
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.4em] mb-2" style={{ color: "#FF6A00" }}>
          TRY IT · ~ 15 SECONDS · TOOLBOX TALK QR FLOW
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight leading-tight">
          Run a real workflow.{" "}
          <span style={{ color: "#FF6A00" }}>Watch the system react.</span>
        </h2>
        <p className="text-sm mt-2 max-w-2xl" style={{ color: "#BDBDBD" }}>
          This is the real workflow your crews use — same form, same routing,
          same backend. Tap through it. The record persists. The system reacts.
        </p>
      </div>

      {/* Step tracker */}
      <div
        className="rounded-xl border-2 overflow-hidden"
        style={{ background: "#0B0B0B", borderColor: "#1F1F1F" }}
      >
        <StepTracker step={step} />

        {/* Body */}
        <div className="px-5 sm:px-8 py-7 sm:py-9 min-h-[340px]">
          {step === 0 && <ScanStep onScan={onScan} />}
          {step === 1 && (
            <PickStep
              job={job}    setJob={setJob}
              crew={crew}  setCrew={setCrew}
              topic={topic} setTopic={setTopic}
              onAdvance={() => setStep(2)}
              allPicked={allPicked}
            />
          )}
          {step === 2 && (
            <ConfirmStep
              job={job} crew={crew} topic={topic}
              submitting={submitting}
              onBack={() => setStep(1)}
              onSubmit={onSubmit}
            />
          )}
          {step === 3 && (
            <CascadeStep cascadeIdx={cascadeIdx} />
          )}
          {step === 4 && (
            <DoneStep elapsed={elapsed} job={job} topic={topic} crew={crew} reset={reset} />
          )}
        </div>
      </div>
    </section>
  );
}

/* ============================================================ */

function StepTracker({ step }) {
  const labels = ["Scan QR", "Pick context", "Complete", "Aftermath"];
  return (
    <div
      className="px-4 sm:px-6 py-3 flex items-center gap-2 sm:gap-3 border-b overflow-x-auto"
      style={{ background: "#070707", borderColor: "#1A1A1A" }}
    >
      {labels.map((label, i) => {
        const active = step === i || (i === 3 && step === 4);
        const done   = step > i || (i === 3 && step === 4);
        return (
          <React.Fragment key={label}>
            <span
              className="inline-flex items-center gap-2 shrink-0 transition-colors"
              data-testid={`tt-step-${i}`}
            >
              <span
                className="inline-flex items-center justify-center w-6 h-6 rounded-full font-mono text-[10px] font-bold"
                style={{
                  background: done ? "#FF6A00" : active ? "rgba(255,106,0,0.18)" : "#0F0F0F",
                  color: done ? "#000" : active ? "#FF6A00" : "#8A8A8A",
                  border: `1px solid ${done || active ? "rgba(255,106,0,0.6)" : "#1F1F1F"}`,
                }}
              >
                {done && i < 3 ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
              </span>
              <span
                className="font-mono text-[10px] uppercase tracking-[0.2em]"
                style={{ color: active ? "#FFFFFF" : done ? "#65DA76" : "#8A8A8A" }}
              >
                {label}
              </span>
            </span>
            {i < labels.length - 1 && (
              <ChevronRight className="w-3.5 h-3.5 shrink-0" style={{ color: "#2A2A2A" }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* STEP 0 — Scan QR */
function ScanStep({ onScan }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: "#FF6A00" }}>
          STEP 1 · QR ACCESS
        </div>
        <h3 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-white leading-tight mb-2">
          Tap the QR. The workflow opens.
        </h3>
        <p className="text-sm" style={{ color: "#BDBDBD" }}>
          On a real jobsite, foremen scan the QR poster on the box-truck or
          trailer. The form opens with the job and crew already wired —
          no logins, no typing.
        </p>
        <ul className="mt-4 space-y-1 text-[12px] font-mono uppercase tracking-[0.15em]" style={{ color: "#8A8A8A" }}>
          <li>· One scan · zero typing</li>
          <li>· Works offline · syncs when reconnected</li>
          <li>· Bilingual EN / ES</li>
        </ul>
      </div>

      <div className="flex flex-col items-center gap-3">
        <button
          type="button"
          onClick={onScan}
          data-testid="tt-scan-btn"
          className="group relative inline-flex items-center justify-center w-44 h-44 rounded-2xl transition-transform hover:scale-[1.02] active:scale-[0.99]"
          style={{
            background: "#0E0E0E",
            border: "2px solid rgba(255,106,0,0.45)",
            boxShadow: "0 0 32px rgba(255,106,0,0.18) inset",
          }}
          aria-label="Scan QR · open Toolbox Talk"
        >
          <QrCode className="w-24 h-24" style={{ color: "#FF6A00" }} />
          <span
            aria-hidden
            className="absolute inset-x-0 top-0 h-0.5"
            style={{ background: "linear-gradient(90deg, transparent, #FF6A00, transparent)" }}
          />
        </button>
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] inline-flex items-center gap-1.5" style={{ color: "#FF6A00" }}>
          <span className="inline-flex w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#FF6A00" }} />
          Tap to scan
        </span>
      </div>
    </div>
  );
}

/* STEP 1 — Pick context */
function PickStep({ job, setJob, crew, setCrew, topic, setTopic, onAdvance, allPicked }) {
  return (
    <div>
      <div className="mb-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: "#FF6A00" }}>
          STEP 2 · PICK CONTEXT
        </div>
        <h3 className="font-display text-xl sm:text-2xl font-black tracking-tight text-white leading-tight">
          Three taps. No typing.
        </h3>
      </div>

      <PickRow label="JOB"      testid="tt-job-row">
        {JOBS.map((j) => (
          <PickTile key={j.number} active={job?.number === j.number} onClick={() => setJob(j)} testid={`tt-job-${j.number}`}>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: "#FF6A00" }}>{j.number}</div>
            <div className="text-[13px] font-bold text-white truncate">{j.name}</div>
          </PickTile>
        ))}
      </PickRow>

      <PickRow label="CREW"     testid="tt-crew-row">
        {CREWS.map((c) => (
          <PickTile key={c.id} active={crew?.id === c.id} onClick={() => setCrew(c)} testid={`tt-crew-${c.id}`}>
            <HardHat className="w-4 h-4 mb-1" style={{ color: "#FFD000" }} />
            <div className="text-[13px] font-bold text-white truncate">{c.label}</div>
          </PickTile>
        ))}
      </PickRow>

      <PickRow label="TOPIC"    testid="tt-topic-row">
        {TOPICS.map((t) => (
          <PickTile key={t.key} active={topic?.key === t.key} onClick={() => setTopic(t)} testid={`tt-topic-${t.key}`}>
            <div className="text-[13px] font-bold text-white truncate">{t.label}</div>
            <div className="text-[10px] truncate" style={{ color: "#8A8A8A" }}>{t.hazards.split(",")[0]}…</div>
          </PickTile>
        ))}
      </PickRow>

      <button
        type="button"
        onClick={onAdvance}
        disabled={!allPicked}
        data-testid="tt-pick-next"
        className="mt-6 w-full h-14 sm:h-16 inline-flex items-center justify-center gap-2 rounded-md font-mono uppercase tracking-[0.25em] text-sm font-bold transition disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          background: allPicked ? "#FF6A00" : "#1A1A1A",
          color:      allPicked ? "#000"     : "#8A8A8A",
          border:    `1px solid ${allPicked ? "#FF6A00" : "#2A2A2A"}`,
          boxShadow:  allPicked ? "0 0 24px rgba(255,106,0,0.25)" : "none",
        }}
      >
        Continue <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function PickRow({ label, testid, children }) {
  return (
    <div className="mb-4" data-testid={testid}>
      <div className="font-mono text-[9px] uppercase tracking-[0.3em] mb-1.5" style={{ color: "#8A8A8A" }}>
        {label}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {children}
      </div>
    </div>
  );
}

function PickTile({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-active={active ? "true" : "false"}
      className="text-left p-3 rounded-md transition active:scale-[0.99]"
      style={{
        background: active ? "rgba(255,106,0,0.12)" : "#0F0F0F",
        border: `1px solid ${active ? "rgba(255,106,0,0.6)" : "#1F1F1F"}`,
        minHeight: 56,
      }}
    >
      {children}
    </button>
  );
}

/* STEP 2 — Confirm */
function ConfirmStep({ job, crew, topic, submitting, onBack, onSubmit }) {
  return (
    <div>
      <div className="mb-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: "#FF6A00" }}>
          STEP 3 · CONFIRM &amp; SUBMIT
        </div>
        <h3 className="font-display text-xl sm:text-2xl font-black tracking-tight text-white leading-tight">
          One tap. The system handles the rest.
        </h3>
      </div>

      <div
        className="rounded-md p-4 mb-5 grid grid-cols-1 sm:grid-cols-3 gap-3"
        style={{ background: "#0F0F0F", border: "1px solid #1F1F1F" }}
      >
        <Summary label="JOB"   primary={job?.number}  secondary={job?.name} />
        <Summary label="CREW"  primary={crew?.label}  secondary={`${crew?.count} attendees · QR-signed`} />
        <Summary label="TOPIC" primary={topic?.label} secondary={topic?.hazards.split(",").slice(0,2).join(",")} />
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          data-testid="tt-back-btn"
          className="h-14 sm:h-16 px-5 rounded-md font-mono uppercase tracking-[0.25em] text-xs"
          style={{ background: "transparent", color: "#BDBDBD", border: "1px solid #2A2A2A" }}
        >
          Back
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          data-testid="tt-submit-btn"
          className="flex-1 h-14 sm:h-16 inline-flex items-center justify-center gap-2 rounded-md font-mono uppercase tracking-[0.25em] text-sm font-bold transition disabled:opacity-60"
          style={{
            background: "#FF6A00",
            color: "#000",
            border: "1px solid #FF6A00",
            boxShadow: "0 0 28px rgba(255,106,0,0.35)",
          }}
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
            </>
          ) : (
            <>
              <CheckCircle2 className="w-4 h-4" /> Complete Toolbox Talk
            </>
          )}
        </button>
      </div>
      <p className="mt-3 text-[11px] font-mono uppercase tracking-[0.2em]" style={{ color: "#8A8A8A" }}>
        On submit · the system notifies · routes · documents · backs up
      </p>
    </div>
  );
}

function Summary({ label, primary, secondary }) {
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-[0.3em] mb-1" style={{ color: "#8A8A8A" }}>
        {label}
      </div>
      <div className="text-sm font-bold text-white truncate">{primary || "—"}</div>
      <div className="text-[11px] truncate" style={{ color: "#BDBDBD" }}>{secondary || ""}</div>
    </div>
  );
}

/* STEP 3 — Aftermath cascade */
function CascadeStep({ cascadeIdx }) {
  return (
    <div>
      <div className="mb-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-2 inline-flex items-center gap-2" style={{ color: "#FF6A00" }}>
          <span className="inline-flex w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#FF6A00" }} />
          STEP 4 · OPERATIONAL AFTERMATH
        </div>
        <h3 className="font-display text-xl sm:text-2xl font-black tracking-tight text-white leading-tight">
          The system is reacting.
        </h3>
        <p className="text-sm mt-1" style={{ color: "#BDBDBD" }}>
          One submission — six automated consequences. No manual email.
          No paperwork delay.
        </p>
      </div>

      <div className="space-y-1.5">
        {CASCADE.map((c, i) => {
          const visible = i < cascadeIdx;
          const Icon = c.icon;
          return (
            <div
              key={c.label}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md border transition-all duration-300 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2"}`}
              style={{
                background: visible ? `${c.color}10` : "transparent",
                borderColor: visible ? `${c.color}45` : "#1A1A1A",
              }}
              data-testid={`tt-cascade-${i}`}
            >
              <span
                className="inline-flex items-center justify-center w-8 h-8 rounded shrink-0"
                style={{ background: "#0A0A0A", color: c.color, border: `1px solid ${c.color}55` }}
              >
                <Icon className="w-4 h-4" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-white truncate leading-tight">{c.label}</div>
                <div className="text-[11px] truncate" style={{ color: "#BDBDBD" }}>{c.sub}</div>
              </div>
              <CheckCircle2
                className="w-4 h-4 shrink-0"
                style={{ color: visible ? c.color : "transparent" }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* STEP 4 — Done */
function DoneStep({ elapsed, job, topic, crew, reset }) {
  return (
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
        style={{ background: "rgba(46,125,50,0.18)", color: "#65DA76", border: "1px solid rgba(46,125,50,0.5)" }}>
        <CheckCircle2 className="w-8 h-8" />
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-1" style={{ color: "#65DA76" }}>
        DONE · ROUTED · BACKED UP
      </div>
      <h3 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-white leading-tight mb-1">
        Toolbox Talk complete in{" "}
        <span style={{ color: "#FF6A00" }}>{elapsed}</span>
      </h3>
      <p className="text-sm max-w-xl mx-auto mb-5" style={{ color: "#BDBDBD" }}>
        {topic?.label} · {crew?.label} · {job?.number}.
        The record is persisted in the live database. Open any portal
        below to find it in PM Folder · Safety Folder · Job Folder.
      </p>
      <div className="flex items-center justify-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={reset}
          data-testid="tt-reset-btn"
          className="h-12 px-5 inline-flex items-center gap-2 rounded-md font-mono uppercase tracking-[0.25em] text-xs"
          style={{ background: "transparent", color: "#BDBDBD", border: "1px solid #2A2A2A" }}
        >
          <RotateCcw className="w-3.5 h-3.5" /> Run again
        </button>
        <a
          href="#roles"
          data-testid="tt-enter-btn"
          className="h-12 px-5 inline-flex items-center gap-2 rounded-md font-mono uppercase tracking-[0.25em] text-xs font-bold"
          style={{ background: "#FF6A00", color: "#000", border: "1px solid #FF6A00" }}
        >
          Step inside the system <ChevronRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}
